<template>
  <section class="py-12">
    <div class="container-wrap">
      <p class="text-sm font-bold text-gray-600">
        {{ tGlobal('products.section-name.overview') }}
      </p>
      <h2 class="text-h2 mb-6">
        {{ productOverview.sectionTitle }}
      </h2>

      <div :class="isShowMediaSection && 'desktop:flex'">
        <div
          v-if="isShowMediaSection"
          class="mb-4 w-full desktop:mb-0 desktop:pr-8"
        >
          <LightboxAlbum
            v-if="isImageOnly"
            :album-name="productBanner.name"
            :album-image-url="thumbnailUrl"
            class="aspect-video w-full overflow-hidden rounded"
          />
          <LightboxVideo
            v-else
            :video-data="videoData"
            class="aspect-video w-full rounded"
          />
        </div>
        <div class="w-full" :class="isShowMediaSection && 'desktop:pl-8'">
          <AreaProductsFeatures />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { EnumProductMediaType } from '@/assets/types/api/Products';
  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';

  const { t: tGlobal } = useI18n({ useScope: 'global' });
  const { isMobile, isTablet } = useScreen();

  const productsStore = useProductsStore();
  const { productOverview, productBanner } = storeToRefs(productsStore);

  const thumbnailUrl = computed((): string => {
    const { mediaWebThumbnail, mediaTabletThumbnail, mediaMobileThumbnail } =
      productOverview.value;
    if (isMobile.value) return mediaMobileThumbnail;
    if (isTablet.value) return mediaTabletThumbnail;
    return mediaWebThumbnail;
  });

  const isImageOnly = computed((): boolean => {
    return productOverview.value.mediaType === EnumProductMediaType.IMAGE;
  });

  const isShowMediaSection = computed((): boolean => {
    if (isImageOnly.value) return !!thumbnailUrl.value;
    return !!productOverview.value.videoUrl;
  });

  const videoUrlPrefix = computed((): string => {
    switch (productOverview.value.mediaType) {
      case EnumProductMediaType.BILIBILI:
        return 'https://player.bilibili.com/player.html?bvid=';
      case EnumProductMediaType.WISTIA:
        return 'https://fast.wistia.net/embed/iframe/';
      // NOTE: 因為 Cookie 隱私權法規的關係，Youtube 影片的網址要改成 nocookie 的網址
      case EnumProductMediaType.YOUTUBE:
        return 'https://www.youtube-nocookie.com/embed/';
      // NOTE: 如果後台不支援 POLYV 的話，這一段可以拿掉
      case EnumProductMediaType.POLYV:
        return 'http://player.polyv.net/videos/player.swf?vid=';
      default:
        return '';
    }
  });

  const videoData = computed((): IVideoData => {
    return {
      title: productBanner.value.name,
      description: productBanner.value.description,
      url: `${videoUrlPrefix.value}${productOverview.value.videoUrl || ''}`,
      imageUrl: thumbnailUrl.value,
      isAutoPlay: true,
    };
  });
</script>

<i18n lang="json">
{
  "en-int": { "title": "Introduction" },
  "en-jp": { "title": "Introduction" },
  "en-tw": { "title": "Introduction" },
  "en-kr": { "title": "Introduction" },
  "en-americas": { "title": "Introduction" },
  "en-asia-pacific": { "title": "Introduction" },
  "en-emea": { "title": "Introduction" },
  "ja-jp": { "title": "紹介" },
  "zh-cn": { "title": "介绍" },
  "zh-tw": { "title": "介紹" }
}
</i18n>
